import { useCallback, useState } from "react";
import { Upload, X, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useUploadFile } from "@/hooks/use-itsm-import";

interface FileUploadZoneProps {
  tenantId: string;
  onUploadComplete: (s3Key: string) => void;
  onUploadingChange?: (uploading: boolean) => void;
  uploadedKeys: string[];
  onRemoveKey: (key: string) => void;
}

export function FileUploadZone({
  tenantId,
  onUploadComplete,
  onUploadingChange,
  uploadedKeys,
  onRemoveKey,
}: FileUploadZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [activeUploads, setActiveUploads] = useState(0);
  const uploadFile = useUploadFile(tenantId);

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const fileArray = Array.from(files);
      setActiveUploads((n) => n + fileArray.length);
      onUploadingChange?.(true);
      for (const file of fileArray) {
        try {
          const s3Key = await uploadFile.mutateAsync(file);
          onUploadComplete(s3Key);
        } finally {
          setActiveUploads((n) => {
            const next = n - 1;
            if (next === 0) onUploadingChange?.(false);
            return next;
          });
        }
      }
    },
    [uploadFile, onUploadComplete, onUploadingChange],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      void handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  return (
    <div className="space-y-3">
      <div
        className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors ${
          isDragging
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/25 hover:border-muted-foreground/50"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => { setIsDragging(false); }}
        onDrop={(e) => { handleDrop(e); }}
      >
        {activeUploads > 0 ? (
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        ) : (
          <Upload className="h-8 w-8 text-muted-foreground" />
        )}
        <p className="mt-2 text-sm text-muted-foreground">
          {activeUploads > 0
            ? "Uploading..."
            : "Drag & drop files here, or click to browse"}
        </p>
        <input
          type="file"
          className="hidden"
          id="file-upload-input"
          multiple
          accept=".jsonl,.gz,.json,.zip"
          onChange={(e) => void handleFiles(e.target.files)}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-3"
          disabled={activeUploads > 0}
          onClick={() => document.getElementById("file-upload-input")?.click()}
        >
          Browse Files
        </Button>
      </div>

      {uploadedKeys.length > 0 && (
        <div className="space-y-2">
          {uploadedKeys.map((key) => (
            <div
              key={key}
              className="flex items-center justify-between rounded-md border px-3 py-2"
            >
              <div className="flex items-center gap-2 overflow-hidden">
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate text-sm">{key.split("/").pop()}</span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => { onRemoveKey(key); }}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
